import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CapitalizePipe } from '@pipes/capitalize.pipe';
import { CALENDAR_CELL_COUNT, CalendarCell, WEEK_LENGTH } from '@models/calendar/calendar.model';
import { DateService } from '@services/date.service';

@Component({
  selector: 'app-modal-date-picker',
  imports: [TranslateModule, CapitalizePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './modal-date-picker.component.html',
})
export class ModalDatePickerComponent {
  private readonly dateService = inject(DateService);

  value = input<string | null>(null);

  dateSelect = output<string>();
  clearClick = output<void>();
  cancelClick = output<void>();

  protected readonly viewedMonth = linkedSignal(() => {
    const reference = this.dateService.parseIsoDate(this.value()) ?? new Date();
    return new Date(reference.getFullYear(), reference.getMonth(), 1);
  });

  protected readonly monthLabel = computed(() =>
    this.dateService.format(this.viewedMonth(), { month: 'long', year: 'numeric' }),
  );

  protected readonly cells = computed<CalendarCell[]>(() => {
    const month = this.viewedMonth();
    const mondayOffset = (month.getDay() + 6) % WEEK_LENGTH;
    const todayIso = this.dateService.toIsoDate(new Date());
    const selectedIso = this.value();

    return Array.from({ length: CALENDAR_CELL_COUNT }, (_, index) => {
      const date = new Date(month.getFullYear(), month.getMonth(), 1 - mondayOffset + index);
      const iso = this.dateService.toIsoDate(date);

      return {
        date,
        iso,
        day: date.getDate(),
        inMonth: date.getMonth() === month.getMonth(),
        isToday: iso === todayIso,
        isSelected: iso === selectedIso,
      };
    });
  });

  protected readonly weekdayLabels = computed(() =>
    this.cells()
      .slice(0, WEEK_LENGTH)
      .map((cell) => this.dateService.format(cell.date, { weekday: 'short' })),
  );

  previousMonth(): void {
    this.#shiftMonth(-1);
  }

  nextMonth(): void {
    this.#shiftMonth(1);
  }

  selectToday(): void {
    this.dateSelect.emit(this.dateService.toIsoDate(new Date()));
  }

  #shiftMonth(delta: number): void {
    this.viewedMonth.update((month) => new Date(month.getFullYear(), month.getMonth() + delta, 1));
  }
}
